/**
 * A map type whose key and value must match. The type parameter U should be a string union type.
 * This is useful when defining "enums" to use at runtime based on string union types,
 * since declaring the source type as a string union allows this type safety for the enum mapping,
 * as well as allowing types to be extended from one another.
 */
export type StringUnionKeyAsValue<U extends string> = Readonly<{
  [K in U]: K
}>;

/**
 * Use in the default clause of a switch statement for a compile-time check that all the cases have been covered
 */
export function assertUnreachable(_: never) {
  return;
}

/**
 * Resolves a value that may be provided directly or as a lazy initializer function.
 *
 * This utility is useful in situations where a parameter can be either a value (`T`)
 * or a function that returns that value (`() => T`), such as with `useState` or custom hooks.
 * It ensures that function-type inputs are invoked once to retrieve the actual value.
 *
 * @template T - The type of the value being resolved.
 * @param valueOrFn - The value to resolve, either directly of type `T`,
 *                    or a function that returns a `T` when called.
 * @returns The resolved value of type `T`.
 *
 * @example
 * const value = resolveValue(42); // 42
 * const lazy = resolveValue(() => computeValue()); // result of computeValue()
 */
export function resolveValue<T>(valueOrFn: T | (() => T)) {
  if (typeof valueOrFn === 'function') {
    return (valueOrFn as () => T)();
  }
  return valueOrFn;
}